import { Link, useNavigate } from 'react-router-dom';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Carte,
  Compteur,
  EnteteEcran,
  Pastille,
  PastilleSite,
  Chargement,
  MessageErreur,
  LigneAlerte,
  MAX_ALERTES_TABLEAU_BORD,
  PiedAlertes,
} from '../components/ui/Primitives';
import { BarreSites } from '../components/ui/BarreSites';
import { usePerimetre } from '../site/usePerimetre';
import { useDashboardCommercial } from '../lib/useDashboardCommercial';
import { useAuth } from '../auth/useAuth';

export function DashboardCommercialPage(): JSX.Element {
  const navigate = useNavigate();
  const { site } = usePerimetre();
  const { utilisateur, peutAcceder } = useAuth();
  const { data, isLoading, error } = useDashboardCommercial(site);

  const recrutement = utilisateur?.profil === 'RECRUTEMENT';

  if (isLoading) return <Chargement libelle="Chargement du tableau de bord..." />;
  if (error || !data) {
    return (
      <div className="p-4 sm:p-6">
        <MessageErreur message="Impossible de charger le tableau de bord." />
      </div>
    );
  }

  const { compteurs, pipeline, accesRapides, alertes } = data;
  const compteurCandidats = (
    <Compteur
      libelle="Candidats à positionner"
      valeur={compteurs.candidatsAPositionner}
      detail={compteurs.detailCandidats}
      ton="warm"
    />
  );

  return (
    <div className="mx-auto max-w-[1500px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre={recrutement ? 'Recrutement candidats' : 'Relation entreprises'}
        sousTitre={
          recrutement
            ? 'Entretiens, suivi des candidats, pièces à fournir, recueil des besoins'
            : 'Prospection, matching, positionnement des candidats'
        }
        actions={
          <>
            <BoutonSecondaire onClick={() => navigate('/calendar')}>Vue calendrier</BoutonSecondaire>
            {recrutement ? (
              <BoutonPrincipal onClick={() => navigate('/com-entretiens')}>
                + Planifier un entretien
              </BoutonPrincipal>
            ) : (
              <BoutonPrincipal onClick={() => navigate('/com-entreprises')}>
                + Nouvelle entreprise
              </BoutonPrincipal>
            )}
          </>
        }
      />

      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Compteur
          libelle="Entretiens cette semaine"
          valeur={compteurs.entretiensCetteSemaine}
          detail={compteurs.detailEntretiens}
          ton="navy"
        />
        <Compteur libelle="Postes ouverts" valeur={compteurs.postesOuverts} ton="mint" />
        {peutAcceder('ped-sanscontrat') ? (
          <Link
            to="/ped-sanscontrat"
            className="block rounded-2xl [&>div]:h-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-codialis-sky"
          >
            {compteurCandidats}
          </Link>
        ) : (
          compteurCandidats
        )}
        <Compteur
          libelle="Taux de placement"
          valeur={compteurs.tauxPlacement ?? '—'}
          ton="blue"
        />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2 min-w-0">
          <Carte titre="Pipeline du jour">
            {pipeline.map((item) => (
              <div
                key={item.id}
                onClick={() => navigate(item.page)}
                className="flex cursor-pointer items-center gap-4 px-4 sm:px-6 py-4 transition hover:bg-gray-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-codialis-navy">{item.titre}</div>
                  <div className="text-[11px] text-codialis-gray">{item.sousTitre}</div>
                </div>
                {item.site && <PastilleSite site={item.site} />}
                <Pastille texte={item.statut} ton={item.ton} />
              </div>
            ))}
          </Carte>

          <Carte titre="Accès rapides" sansBordure>
            <div className="grid grid-cols-1 gap-3 p-4 sm:p-6 sm:grid-cols-2">
              {accesRapides.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => navigate(item.page)}
                  className={`rounded-xl bg-gray-50 p-4 text-left transition hover:bg-gray-100 ${
                    index === accesRapides.length - 1 && accesRapides.length % 2 === 1
                      ? 'sm:col-span-2'
                      : ''
                  }`}
                >
                  <div className="text-sm font-semibold text-codialis-navy">{item.titre}</div>
                  <div className="mt-0.5 text-[11px] text-codialis-gray">{item.sousTitre}</div>
                </button>
              ))}
            </div>
          </Carte>
        </div>

        <div className="space-y-6">
          <Carte
            titre="Mes alertes"
            actions={
              <BoutonSecondaire onClick={() => navigate('/alertes')}>
                Module d'alertes
              </BoutonSecondaire>
            }
          >
            {alertes.slice(0, MAX_ALERTES_TABLEAU_BORD).map((item) => (
              <LigneAlerte
                key={item.id}
                titre={item.titre}
                detail={item.sousTitre}
                ton={item.ton}
              />
            ))}
            <PiedAlertes
              restantes={alertes.length - MAX_ALERTES_TABLEAU_BORD}
              onClick={() => navigate('/alertes')}
            />
          </Carte>
        </div>
      </div>
    </div>
  );
}
